import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import Box from '@mui/material/Box';
import MuiSkeleton, { type SkeletonProps as MuiSkeletonProps } from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

import { cssVar } from '../../theme/tokens';
import { mergeSx } from './sizes';

export interface SkeletonProps extends Omit<MuiSkeletonProps, 'variant'> {
  /** text (a line), block (a card or image) or circle (an avatar). */
  shape?: 'text' | 'block' | 'circle';
}

/** A placeholder while content loads; respects reduced motion (base.css stops the animation). */
export function Skeleton({ shape = 'text', sx, ...props }: SkeletonProps) {
  return (
    <MuiSkeleton
      variant={shape === 'block' ? 'rounded' : shape === 'circle' ? 'circular' : 'text'}
      sx={mergeSx(shape === 'block' && { borderRadius: cssVar('radius-md') }, sx)}
      {...props}
    />
  );
}

export interface EmptyStateProps {
  title: ReactNode;
  description?: ReactNode;
  /** Decorative icon (default: an empty tray). */
  icon?: ReactNode;
  /** A button that resolves the empty state ("Create video", "Clear filters"). */
  action?: ReactNode;
  /** Less padding, for table bodies and small cards. */
  compact?: boolean;
}

/** What to show when there is nothing to list yet, or no match for the filters. */
export function EmptyState({ title, description, icon, action, compact = false }: EmptyStateProps) {
  return (
    <Stack
      sx={{
        alignItems: 'center',
        textAlign: 'center',
        gap: 2.5,
        py: compact ? 8 : 14,
        px: 6,
        color: cssVar('text-subtle'),
      }}
    >
      <Box
        aria-hidden
        sx={{
          display: 'grid',
          placeItems: 'center',
          width: cssVar('space-12'),
          height: cssVar('space-12'),
          borderRadius: cssVar('radius-lg'),
          bgcolor: cssVar('tile'),
          '& .MuiSvgIcon-root': { fontSize: cssVar('space-6') },
        }}
      >
        {icon ?? <InboxRoundedIcon />}
      </Box>
      <Typography sx={{ color: cssVar('text'), fontWeight: 800 }}>{title}</Typography>
      {description ? <Typography variant="bodySm">{description}</Typography> : null}
      {action ? <Box sx={{ mt: 2 }}>{action}</Box> : null}
    </Stack>
  );
}
